import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Smartphone } from "lucide-react";
import { Logo } from "./Logo";
import { PLAY_URL, IMAGES } from "./config";

const LINES = ["FORGE YOUR", "LEGEND.", "CONQUER", "THE REALM."];

const lineVariant = {
  hidden: { y: "110%", rotate: 3 },
  visible: (i) => ({
    y: "0%",
    rotate: 0,
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.5 + i * 0.14 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 pt-24 pb-16"
    >
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 z-0 scale-110"
      >
        <img
          src={IMAGES.heroCastle}
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-[#080808]" />
      </motion.div>
      <div className="torch-glow absolute left-1/2 top-1/3 z-0 h-[520px] w-[820px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(255,140,0,0.12),transparent_65%)]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 flex flex-col items-center text-center">
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.25 }}
        >
          <Logo size={64} className="mb-8 drop-shadow-[0_0_30px_rgba(212,175,55,0.35)]" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, letterSpacing: "0.8em" }}
          animate={{ opacity: 1, letterSpacing: "0.45em" }}
          transition={{ duration: 1.2, delay: 0.4 }}
          data-testid="hero-title"
          className="font-accent mb-10 text-sm font-bold text-[#D4AF37] md:text-base"
        >
          IRONCREST
        </motion.p>

        <h1 className="font-display text-[clamp(3rem,9vw,7.5rem)] font-semibold leading-[0.95] tracking-tight text-[#EAEAEA]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
                className="block origin-left"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.5 }}
          data-testid="hero-subtitle"
          className="mt-10 max-w-xl text-base text-[#A3A3A3] md:text-lg"
        >
          A text-based medieval MMORPG. Build your kingdom, raise armies, forge
          alliances and battle thousands of rivals for control of the realm.
        </motion.p>

        <motion.a
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.7 }}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          href={PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-play-button"
          className="group mt-12 flex items-center gap-4 rounded-2xl bg-gradient-to-b from-[#E7C65A] to-[#B8912B] px-8 py-4 text-[#1A1208] shadow-[0_0_50px_rgba(212,175,55,0.3)] transition-shadow duration-300 hover:shadow-[0_0_80px_rgba(212,175,55,0.5)]"
        >
          <Smartphone className="h-6 w-6" />
          <span className="text-left">
            <span className="font-accent block text-sm font-bold tracking-wider">
              COMING SOON
            </span>
            <span className="block text-xs font-medium opacity-80">
              on Google Play
            </span>
          </span>
        </motion.a>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="h-14 w-px bg-gradient-to-b from-transparent via-[#D4AF37]/60 to-transparent" />
      </motion.div>
    </section>
  );
};
